// Account surface. Profile card up top, then grouped sections for
// Subscription, Settings, Privacy & Security, Help, About. Sign out at
// the bottom in its own destructive-action card. The intent is a feel
// closer to Apple Settings than a single CRUD form.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserAvatar } from '../components/UserAvatar';
import { useSession } from '../hooks/SessionProvider';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import {
  DEMO_DISPLAY_EMAIL,
  DEMO_DISPLAY_NAME,
  isE2EIdentity,
  useDemoMode,
} from '../hooks/useDemoMode';
import { firstNameFromEmail, useMe } from '../hooks/useMe';
import { AppPage } from '../layouts/AppPage';

const TOOL_LINKS: Array<{ to: string; label: string; sub: string }> = [
  // Doctor visit summary, fridge sheet, wallet card, TSA letter all
  // live on /medications. Round-4 personas (Marisol, Tanya, Leona)
  // couldn't find the doctor-visit PDF because it was only surfaced
  // there. Lift the most-used entry point so it shows up in the tool
  // list a senior actually scans.
  {
    to: '/allergies',
    label: 'Allergies and sensitivities',
    sub: 'Tell Siggy what to watch for before any new medicine',
  },
  { to: '/medications', label: 'Doctor visit summary', sub: 'Print or email to your provider' },
  { to: '/care-plan', label: 'Your week', sub: 'A short recap from Siggy' },
  { to: '/pillbox', label: 'Daily pillbox', sub: 'Check off doses, build a streak' },
  { to: '/symptoms', label: 'Symptom check-in', sub: 'Log how you feel each day' },
  { to: '/interactions', label: 'Interaction graph', sub: 'Visual safety map' },
  { to: '/food', label: 'Food and meals', sub: 'Drug-food interactions + safe meals' },
  { to: '/pill-identify', label: 'Identify a pill', sub: 'Camera-based lookup' },
  { to: '/refills', label: 'Refills', sub: 'Days supply, running-low alerts' },
  { to: '/reminders', label: 'Reminders', sub: 'Email reminders for each dose' },
  { to: '/family', label: 'Family', sub: 'Invite a caregiver' },
  { to: '/doctors', label: 'My doctors', sub: 'Primary care, specialists, pharmacy' },
  // Round-3 fix #13: Videos tile removed from Home grid + tools list
  // until the first weekly video lands. Page route is still registered
  // in App.tsx for deep-links. Re-enable this row when /api/videos/recent
  // returns at least one row.
  { to: '/settings', label: 'Settings', sub: 'Text size, contrast, time zone' },
  { to: '/connect', label: 'Connect', sub: 'Pharmacy, Apple Health, EHR, coupons' },
  { to: '/shopping', label: 'Pharmacy list', sub: "Don't forget your pickups + supplies" },
];

const TIER_LABEL: Record<string, string> = {
  FREE: 'Free',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
  FAMILY: 'Family',
};

export function Account(): JSX.Element {
  const { clear } = useSession();
  const { me } = useMe();
  // Fall back to the email local-part when firstNameFromEmail returns
  // empty (test accounts, placeholders), so the profile card never reads
  // the literal 'You' for a real signed-in user.
  // Round-3 fix #18: displayName (user-controlled) wins, then the
  // email-derived first name, then the email local-part. Final fallback
  // 'You' for ghost/placeholder accounts.
  const demoMode = useDemoMode();
  const demoSwap = demoMode && isE2EIdentity(me?.email);
  const derivedFirst = me?.email ? firstNameFromEmail(me.email) : '';
  const firstName = demoSwap
    ? DEMO_DISPLAY_NAME
    : me?.displayName || derivedFirst || me?.email?.split('@')[0] || 'You';
  const displayEmail = demoSwap ? DEMO_DISPLAY_EMAIL : (me?.email ?? null);
  const tier = me?.tier ?? 'FREE';

  return (
    <AppPage title="Account">
      <section className="account-header" aria-label="Profile">
        <UserAvatar email={displayEmail} size={72} />
        <div>
          <DisplayNameField current={firstName} />
          {displayEmail ? <p className="account-header__email">{displayEmail}</p> : null}
          <span className="account-header__badge">{TIER_LABEL[tier] ?? 'Free'} plan</span>
        </div>
      </section>

      {tier === 'FREE' ? (
        <section className="upsell-card" aria-label="Upgrade">
          <h3 className="upsell-card__title">Compare plans</h3>
          <ul
            className="upsell-card__body"
            style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'grid', gap: 10 }}
          >
            <li>
              <strong>Free.</strong> Your current plan. Track meds, see safety alerts, ask Siggy.
            </li>
            <li>
              <strong>Premium: $9.99/mo</strong> (or $99/yr). Adds unlimited Ask Siggy chats and a
              monthly video visit with Dr. Marlee Grounds, PharmD.
            </li>
            <li>
              <strong>Family: $14.99/mo</strong> (or $149/yr). Adds everything in Premium plus a
              shared plan for up to six loved ones.
            </li>
          </ul>
          <a
            href="https://www.otchealthmart.com/pages/medreview-plans"
            className="upsell-card__cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Compare plans
          </a>
        </section>
      ) : null}

      <section className="account-section" aria-labelledby="settings-title">
        <h3 id="settings-title" className="account-section__title">
          Settings
        </h3>
        <div className="account-row">
          <span className="account-row__label">Text size</span>
          <span className="account-row__value">
            {me?.accessibility.fontSize === 'xl'
              ? 'Largest'
              : me?.accessibility.fontSize === 'lg'
                ? 'Larger'
                : 'Normal'}
          </span>
        </div>
        <div className="account-row">
          <span className="account-row__label">High contrast</span>
          <span className="account-row__value">
            {me?.accessibility.highContrast ? 'On' : 'Off'}
          </span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Reduced motion</span>
          <span className="account-row__value">
            {me?.accessibility.reducedMotion ? 'On' : 'Off'}
          </span>
        </div>
      </section>

      <section className="account-section" aria-labelledby="privacy-title">
        <h3 id="privacy-title" className="account-section__title">
          Privacy &amp; security
        </h3>
        <div className="account-row">
          <span className="account-row__label">Data storage</span>
          <span className="account-row__value">HIPAA compliant</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">
            Sign-in method
            <span style={{ display: 'block', fontSize: 14, color: 'var(--color-text-secondary)' }}>
              We email you a one-tap link. No password to remember. Stays signed in on this device
              for 30 days.
            </span>
          </span>
          <span className="account-row__value">Email link</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Privacy policy</span>
          <a
            className="account-link"
            href="https://www.otchealthmart.com/policies/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read
          </a>
        </div>
      </section>

      <DataRightsSection />

      <section className="account-section" aria-labelledby="tools-title">
        <h3 id="tools-title" className="account-section__title">
          All tools
        </h3>
        <ul className="tool-link-list">
          {TOOL_LINKS.map((t) => (
            <li key={t.to}>
              <Link to={t.to} className="tool-link">
                <span>
                  <span className="tool-link__label">{t.label}</span>
                  <span className="tool-link__sub">{t.sub}</span>
                </span>
                <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    d="M7 4l6 6-6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <PaperworkSection />

      <section className="account-section" aria-labelledby="help-title">
        <h3 id="help-title" className="account-section__title">
          Help &amp; support
        </h3>
        <div className="account-row">
          <span className="account-row__label">Email support</span>
          <a className="account-link" href="mailto:support@otchealthmart.com">
            support@otchealthmart.com
          </a>
        </div>
        <div className="account-row">
          <span className="account-row__label">Frequently asked questions</span>
          <a
            className="account-link"
            href="https://www.otchealthmart.com/pages/faq"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </a>
        </div>
      </section>

      <section className="account-section" aria-labelledby="about-title">
        <h3 id="about-title" className="account-section__title">
          About MedReview
        </h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          MedReview comes from OTCHealthMart and Dr. Marlee Grounds. It is for learning. It does not
          replace your doctor or pharmacist.
        </p>
      </section>

      <section className="account-section" aria-label="Sign out">
        <button type="button" className="account-cta" onClick={clear} data-testid="sign-out">
          Sign out
        </button>
      </section>
    </AppPage>
  );
}

// Round-3 fix #18: user-controlled display name on the Account header.
// Inline pencil-icon edit; saves via PATCH /api/me/display-name. The
// page reads me.displayName, falling back to the email-derived first
// name when unset (handled in Account()'s firstName derivation).
function DisplayNameField({ current }: { current: string }): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    const next = value.trim();
    if (!next || next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await authedFetch('/api/me/display-name', {
        method: 'PATCH',
        body: JSON.stringify({ displayName: next }),
      });
      // Optimistic; useMe will re-read on next page hit. For instant
      // header feedback we could force a refetch; deferred.
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Display name"
          maxLength={60}
          style={{
            padding: '6px 10px',
            fontSize: 22,
            fontWeight: 700,
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
          }}
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="account-link"
          style={{ background: 'transparent', border: 0, cursor: 'pointer', fontWeight: 600 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </span>
    );
  }

  return (
    <p className="account-header__name">
      {current}{' '}
      <button
        type="button"
        onClick={() => {
          setValue(current);
          setEditing(true);
        }}
        aria-label="Edit display name"
        style={{
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          fontSize: 14,
          marginLeft: 6,
        }}
      >
        Edit
      </button>
    </p>
  );
}

function DataRightsSection(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [busy, setBusy] = useState<'export' | 'delete' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onExport = async () => {
    if (busy) return;
    setBusy('export');
    setMessage(null);
    try {
      const res = await authedFetch('/api/me/export');
      if (!res.ok) {
        setMessage('Could not build your data export. Try again in a minute.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medreview-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Your data was downloaded as a JSON file.');
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (busy) return;
    setBusy('delete');
    setMessage(null);
    try {
      const res = await authedFetch('/api/me', { method: 'DELETE' });
      const json = (await res.json()) as { ok: boolean; data?: { message: string } };
      if (json.ok && json.data) {
        setMessage(
          `${json.data.message} Tap "Cancel deletion" within 7 days to keep your account.`,
        );
      } else {
        setMessage('Could not start account deletion. Try again or email support.');
      }
      setConfirmDelete(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="account-section" aria-labelledby="data-rights-title">
      <h3 id="data-rights-title" className="account-section__title">
        Your data
      </h3>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        You can take a copy of your data with you, or delete it.
      </p>
      <div className="account-row">
        <span className="account-row__label">Download my data (JSON)</span>
        <button
          type="button"
          className="account-link"
          onClick={() => void onExport()}
          disabled={busy === 'export'}
          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          {busy === 'export' ? 'Preparing...' : 'Download'}
        </button>
      </div>
      <div className="account-row">
        <span className="account-row__label">Delete my account</span>
        {confirmDelete ? (
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button
              type="button"
              className="account-link"
              onClick={() => void onDelete()}
              disabled={busy === 'delete'}
              style={{
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                color: 'var(--color-danger)',
                fontWeight: 700,
              }}
            >
              {busy === 'delete' ? 'Submitting...' : 'Confirm delete'}
            </button>
            <button
              type="button"
              className="account-link"
              onClick={() => setConfirmDelete(false)}
              disabled={busy === 'delete'}
              style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="account-link"
            onClick={() => setConfirmDelete(true)}
            style={{
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              color: 'var(--color-danger)',
            }}
          >
            Delete account
          </button>
        )}
      </div>
      {message ? (
        <p style={{ marginTop: 8, fontSize: 14, color: 'var(--color-text-primary)' }}>{message}</p>
      ) : null}
    </section>
  );
}

function PaperworkSection(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [busy, setBusy] = useState<'wallet' | 'travel' | 'fridge' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const generate = async (kind: 'wallet' | 'travel' | 'fridge') => {
    if (busy) return;
    setErr(null);
    setBusy(kind);
    try {
      const path =
        kind === 'wallet'
          ? '/api/pdf/wallet-card'
          : kind === 'travel'
            ? '/api/pdf/travel-letter'
            : '/api/pdf/fridge-sheet';
      const res = await authedFetch(path, { method: 'POST', body: '{}' });
      const json = (await res.json()) as {
        ok: boolean;
        data?: { downloadUrl: string };
        error?: { message: string };
      };
      if (!json.ok || !json.data?.downloadUrl) {
        setErr(json.error?.message ?? 'Could not build the PDF.');
        return;
      }
      // Open in new tab; on iOS Safari this previews in the in-app PDF
      // viewer and lets the user save or AirDrop. Focus group #20
      // (Doris, Walter): they wanted preview before download, this is
      // the OS-native preview path.
      window.open(json.data.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="account-section" aria-labelledby="paperwork-title">
      <h3 id="paperwork-title" className="account-section__title">
        Printable paperwork
      </h3>
      <div className="account-row">
        <span className="account-row__label">Emergency wallet card</span>
        <button
          type="button"
          className="account-link"
          onClick={() => void generate('wallet')}
          disabled={busy === 'wallet'}
          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          {busy === 'wallet' ? 'Building...' : 'Download PDF'}
        </button>
      </div>
      <div className="account-row">
        <span className="account-row__label">Travel medication letter (TSA / customs)</span>
        <button
          type="button"
          className="account-link"
          onClick={() => void generate('travel')}
          disabled={busy === 'travel'}
          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          {busy === 'travel' ? 'Building...' : 'Download PDF'}
        </button>
      </div>
      <div className="account-row">
        <span className="account-row__label">
          Fridge sheet (large print)
          <span style={{ display: 'block', fontSize: 14, color: 'var(--color-text-secondary)' }}>
            One page, 18 point text, name plus dose plus time. Stick it on the fridge.
          </span>
        </span>
        <button
          type="button"
          className="account-link"
          onClick={() => void generate('fridge')}
          disabled={busy === 'fridge'}
          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          {busy === 'fridge' ? 'Building...' : 'Download PDF'}
        </button>
      </div>
      {err ? (
        <p style={{ color: 'var(--color-danger)', fontSize: 16, margin: '8px 0 0' }}>{err}</p>
      ) : null}
    </section>
  );
}
